import { Sidebar, Menu, MenuItem, SubMenu } from "react-pro-sidebar";
import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { CustomNavLink } from "src/components/Common/CustomLink";
import { useTranslation } from "react-i18next";
import APP_ROUTES from "src/routes/routes";
import logo from "src/assets/images/logo.png";
import { useRecoilState, useSetRecoilState, useRecoilValue } from "recoil";
import { sidebarState } from "src/recoil/atoms/sidebar";
import ThrustIcon from "src/assets/images/thrust_icon.svg";
import GncIcon from "src/assets/images/gnc.svg";
import { resetFormPromptState, projectResourcesState } from "src/recoil/atoms/global";
import { REACT_APP_PORTAL_URL } from "src/constant/environment";
import { RESOURCE_TYPE } from "src/constant/common";
import useSubmenuHover from "src/Hooks/useSubmenuHover";
import useScrollbarDetection from "src/Hooks/useScrollbarDetection";

const SidebarHeader = ({ children, needReset, ...rest }) => {
  const { t } = useTranslation("topbar");

  return (
    <div className="sidebar-header m-b-3 m-t-3">
      <div style={{ display: "flex", alignItems: "center" }}>
        <a href={REACT_APP_PORTAL_URL} target="_blank" rel="noreferrer">
          <img src={logo} alt="" height="48" />
        </a>
        <div className="fw-bold m-l-10" style={{ textWrap: "nowrap" }}>
          {t("title")}
        </div>
      </div>
    </div>
  );
};

const SidebarMenu = () => {
  const { t } = useTranslation("sidebar");
  const [sidebar, setSidebar] = useRecoilState(sidebarState);
  const projectResources = useRecoilValue(projectResourcesState);
  const [broken, setBroken] = useState(false);
  const location = useLocation();
  const isMenuItemActive = (to) => location.pathname === to;
  const setResetFormState = useSetRecoilState(resetFormPromptState);

  const hasScrollbar = useScrollbarDetection(".ps-sidebar-root .ps-sidebar-container", [sidebar.collapsed, projectResources]);
  const {
    hoveredSubmenu,
    handleSubmenuMouseEnter,
    handleSubmenuMouseLeave,
    handleSidebarMouseLeave,
    handleMenuItemMouseEnter,
    handleMenuItemMouseLeave,
  } = useSubmenuHover(sidebar.collapsed, ".ps-sidebar-root");

  useEffect(() => {
    setSidebar({ ...sidebar, broken: broken });
  }, [broken]);

  // const firstSubMenuItems = [
  //   { to: APP_ROUTES.AERODYNAMICS, label: t("ar11") },
  //   { to: "", label: t("ta10") },
  //   { to: "", label: t("lv10") },
  //   { to: "", label: t("cf10") },
  // ];

  const testAnalysisSubMenuItems = [
    { to: APP_ROUTES.TEST_ANALYSIS_LIST, label: t("ta10_isas") },
    { to: APP_ROUTES.WIND_TUNNEL_TEST_NAL, label: t("ta10_nal") },
  ];

  const secondSubMenuItems = [
    { to: APP_ROUTES.ENGINE_COMPONENT_LIST, label: t("ec10") },
    { to: APP_ROUTES.THRUST_CALCULATION_LIST, label: t("tc11") },
  ];

  const gncSimulator = [
    {
      to: APP_ROUTES.GNC_SIMULATOR,
      label: t("gnc_gn10"),
    },
    {
      to: APP_ROUTES.GNC_SIMULATOR_6DOF,
      label: t("gnc_gs10"),
    },
  ];

  const renderSubMenu = useCallback(
    (menuList, submenuId) =>
      menuList.map((item, index) =>
        item.to ? (
          <MenuItem
            key={index}
            component={<CustomNavLink to={item.to} />}
            onMouseEnter={() => handleMenuItemMouseEnter(submenuId)}
            onMouseLeave={handleMenuItemMouseLeave}
          >
            {" "}
            {item.label}
          </MenuItem>
        ) : (
          <MenuItem key={index} disabled onMouseEnter={() => handleMenuItemMouseEnter(submenuId)}>
            {" "}
            {item.label}
          </MenuItem>
        )
      ),
    [handleMenuItemMouseEnter, handleMenuItemMouseLeave]
  );

  const isShowMenu = (resourceType) => {
    return projectResources.includes(resourceType);
  };

  return (
    <React.Fragment>
      <Sidebar
        collapsed={sidebar.collapsed}
        toggled={sidebar.toggled}
        onBackdropClick={() => setSidebar({ ...sidebar, toggled: false })}
        backgroundColor="white"
        onBreakPoint={setBroken}
        breakPoint="lg"
        onMouseLeave={handleSidebarMouseLeave}
        {...(hasScrollbar && { width: "260px" })}
        {...(hasScrollbar && { collapsedWidth: "100px" })} // Only set collapsedWidth when scrollbar is displayed to prevent icon overlap
      >
        <SidebarHeader needReset={isMenuItemActive(APP_ROUTES.HOME)} />
        <Menu renderExpandIcon={({ open }) => <i className={`bi ${open ? "bi-chevron-down" : "bi-chevron-right"} fs-12`}></i>}>
          {isShowMenu(RESOURCE_TYPE.LABOR_SAVING) && (
            <MenuItem icon={<i className="bi bi-rocket fs-20"></i>} component={<CustomNavLink to={APP_ROUTES.ROCKET_MODEL} />}>
              {" "}
              {t("ro10")}
            </MenuItem>
          )}
          {isShowMenu(RESOURCE_TYPE.FLIGHT_SIMULATION) && (
            <MenuItem
              icon={<i className="bi bi-rocket-takeoff fs-20"></i>}
              component={
                <CustomNavLink
                  to={APP_ROUTES.HOME}
                  onClick={() => {
                    setResetFormState(true);
                  }}
                />
              }
            >
              {" "}
              {t("fs10")}
            </MenuItem>
          )}
          {isShowMenu(RESOURCE_TYPE.TEST_ANALYSIS) && (
            <SubMenu
              icon={<i className="bi bi-graph-up fs-20"></i>}
              label={t("ta10")}
              className={`${
                sidebar.collapsed ? `hoverable-submenu ${hoveredSubmenu === RESOURCE_TYPE.TEST_ANALYSIS ? "show-submenu" : ""}` : ""
              }`}
              component={
                <CustomNavLink
                  to={APP_ROUTES.TEST_ANALYSIS_LIST}
                  className={`${testAnalysisSubMenuItems.some((item) => isMenuItemActive(item.to)) ? "active" : ""}`}
                />
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseEnter={() => handleSubmenuMouseEnter(RESOURCE_TYPE.TEST_ANALYSIS)}
              onMouseLeave={handleSubmenuMouseLeave}
            >
              {renderSubMenu(testAnalysisSubMenuItems, RESOURCE_TYPE.TEST_ANALYSIS)}
            </SubMenu>
          )}
          {/* {
            isShowMenu(RESOURCE_TYPE.TEST_ANALYSIS) && (
              <SubMenu
                icon={<i className="bi bi-wind fs-20"></i>}
                label={t("ar10")}
                className={`${sidebar.collapsed ? "hoverable-submenu" : ""}`}
                component={
                  <CustomNavLink
                    to={APP_ROUTES.AERODYNAMICS}
                    className={`${firstSubMenuItems.some((item) => isMenuItemActive(item.to)) ? "active" : ""}`}
                  />
                }
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {renderSubMenu(firstSubMenuItems)}
              </SubMenu>
            )
          } */}
          {isShowMenu(RESOURCE_TYPE.LABOR_SAVING) && (
            <SubMenu
              icon={<img src={ThrustIcon} alt="thrust_icon" />}
              label={t("tc10")}
              className={`${
                sidebar.collapsed ? `hoverable-submenu ${hoveredSubmenu === RESOURCE_TYPE.LABOR_SAVING ? "show-submenu" : ""}` : ""
              }`}
              component={
                <CustomNavLink
                  to={APP_ROUTES.ENGINE_COMPONENT_LIST}
                  className={`${secondSubMenuItems.some((item) => isMenuItemActive(item.to)) ? "active" : ""}`}
                />
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseEnter={() => handleSubmenuMouseEnter(RESOURCE_TYPE.LABOR_SAVING)}
              onMouseLeave={handleSubmenuMouseLeave}
            >
              {renderSubMenu(secondSubMenuItems, RESOURCE_TYPE.LABOR_SAVING)}
            </SubMenu>
          )}
          {isShowMenu(RESOURCE_TYPE.GNC_SIMULATION) && (
            <SubMenu
              icon={<img src={GncIcon} alt="gnc_icon" className="width-by-px-25 m-l-5-n" />}
              label={t("gnc")}
              className={`${
                sidebar.collapsed ? `hoverable-submenu ${hoveredSubmenu === RESOURCE_TYPE.GNC_SIMULATION ? "show-submenu" : ""}` : ""
              }`}
              component={
                <CustomNavLink
                  to={APP_ROUTES.GNC_SIMULATOR}
                  className={`${gncSimulator.some((item) => isMenuItemActive(item.to)) ? "active" : ""}`}
                />
              }
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseEnter={() => handleSubmenuMouseEnter(RESOURCE_TYPE.GNC_SIMULATION)}
              onMouseLeave={handleSubmenuMouseLeave}
            >
              {renderSubMenu(gncSimulator, RESOURCE_TYPE.GNC_SIMULATION)}
            </SubMenu>
          )}
        </Menu>
      </Sidebar>
    </React.Fragment>
  );
};

export default SidebarMenu;
